import { IRoleFormValues } from 'models/role';
import { IFRTCalibreFormValues } from 'models/frt';
import { CheckboxStyle } from 'components/molecules/FormCheckbox/styles';
import { IDictionary, IDictionaryValueFormValues } from 'models/dictionary';
import { IShortTransaction } from 'models/transaction';
import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { TypographyVariant } from '@mui/material/styles';
import { EUserStatus } from 'pages/UsersPage/UserSearchForm';
import type { ELegalAttachmentsGroupBy } from 'models/legalEntity';

export const initialExampleFormValues = {
  input: '',
  textarea: '',
  checkbox: true,
  switch: false,
  radioGroup: 'male',
  select: 'one',
  datepicker: null,
};

export const initialLoginFormValues = {
  email: '',
  password: '',
};

export const initialForgotPasswordFormValues = {
  email: '',
};

export const initialResetPasswordFormValues = {
  newPassword: '',
  newPasswordRepeat: '',
};

export const initialTwoFactorAuthFormValues: ITwoFactorAuthForm = {
  authCode: '',
  isRememberDevice: false,
  trustedDeviceTimeOptionUniqueId: undefined,
};

export interface IAuthenticateForm {
  code: string[];
  trustedDeviceTimeOptionUniqueId?: string;
  isRememberDevice: boolean;
}

export const initialAuthenticateFormValues: IAuthenticateForm = {
  code: [],
  trustedDeviceTimeOptionUniqueId: undefined,
  isRememberDevice: false,
};
export interface ITwoFactorAuthForm {
  authCode: string;
  isRememberDevice: boolean;
  trustedDeviceTimeOptionUniqueId: string | undefined;
}

export const initialRoleFormValues: IRoleFormValues = {
  name: '',
  description: '',
  formPermissionIds: {},
  permissionIds: [],
};

export const initialDictionaryValueFormValues: IDictionaryValueFormValues = {
  isActive: 'true',
  translations: {},
};

export interface IFormElementProps {
  name: string;
  id?: string;
  label?: string;
  control: any;
  errors: any;
  setError?: any;
  clearErrors?: any;
  withValidation?: boolean;
  disabled?: boolean;
  style?: CheckboxStyle;
  onChange?: (arg: boolean, item?: string) => void;
  labelVariant?: TypographyVariant;
  labelEnable?: boolean;
  labelWeight?: number;
  labelColor?: string;
  checked?: boolean;
  booleanType?: boolean;
  onChangeStatus?: (arg: boolean, item?: string) => void | boolean;
}

export interface IFormElementOptionProp {
  label: string;
  value: string | number;
  isArchived?: boolean;
}
export interface IFormElementOptionHelperTextProp {
  text: string;
  value: string | number;
}

export const initialLegalEntityFormValues = {
  firstName: '',
  lastName: '',
  secondSurname: '',
  dateOfBirth: null,
  genderUniqueId: '',
  visibilityUniqueId: '',
  prefixGroupUniqueId: '',
  prefixValueUniqueId: '',
  city: '',
  nationalityUniqueId: '',
  placeOfBirth: '',
  height: '',
};

export const initialOrgLegalEntityFormValues = {
  organizationName: '',
  organisationTypeUniqueIds: [],
  organisationCategoryUniqueId: '',
  departmentName: '',
  visibilityUniqueId: '',
  city: '',
};

export const initialCommentFormValues = {
  title: '',
  comment: '',
};

export const initialAddressFormValues = {
  isDefault: true,
  storage: true,
  typeUniqueId: '',
  line1: '',
  line2: '',
  postalCode: '',
  city: '',
  regionUniqueId: '',
  countryUniqueId: '',
  latitude: '',
  longitude: '',
};

export const initialLegalEntityIndividualContactFormValues = {
  email: '',
  codeDictionaryUniqueId: '',
  value: '',
};

export const initialLegalEntityOrganizationContactFormValues = {
  name: '',
  role: '',
  email: '',
  codeDictionaryUniqueId: '',
  value: '',
  associatedIndividualUniqueId: '',
};

export const initialLegalEntityPreviousNamesSurnamesFormValues = {
  legalUniqueId: '',
  firstName: '',
  middleNames: '',
  lastName: '',
};

export interface ISearchGroupFormValues {
  firearmSerialNumber: string;
}

export const initialGroupSearchFormValues: ISearchGroupFormValues = {
  firearmSerialNumber: '',
};

export interface ISearchFirearmFormValues {
  legalUniqueId?: string;
  groupUniqueId?: string;
  serialNumber: string;
  modelUniqueId: string;
  makeUniqueId: string;
  caliberUniqueId: string;
}

export interface ISearchAncillaryFormValues {
  ancillarySpecificTypeUniqueId: string;
  ancillaryType: string;
  firearmType: string;
  serialNumber: string;
  lotNumber: string;
  batchNumber: string;
  manufacturersUniqueIds: string[];
}

export const initialFirearmSearchFormValues: ISearchFirearmFormValues = {
  legalUniqueId: '',
  serialNumber: '',
  modelUniqueId: '',
  makeUniqueId: '',
  caliberUniqueId: '',
};

export interface ISearchAncillariesFormValues {
  preset: string | undefined;
  artId: string;
  ancillaryDictionaryUniqueId: string;
  lotNumber: string;
  batchNumber: string;
  factoriesUniqueIds: string[];
  manufactureDateFrom: Date | string | null;
  manufactureDateTo: Date | string | null;
  functionalTypesUniqueIds: string[];
  cartridgeCompositionsUniqueIds: string[];
  coloursOfPrimerSealantUniqueIds: string[];
  projectileShapesUniqueIds: string[];
  shotSizesUniqueIds: string[];
  munitionTypesUniqueIds: string[];
  munitionVelocitiesUniqueIds: string[];
  headstampYear: number | string;
  alphanumericalMarkins: string;
  hostTypesUniqueIds: string[];
  magnificationsUniqueIds: string[];
  productName: string;
  utilitiesUniqueIds: string[];
  reticlesUniqueIds: string[];
  lightColoursUniqueIds: string[];
  accessoryCapacities: string[];
  frtTypesUniqueIds: string[];
  frtMakesUniqueIds: string[];
  frtModelsUniqueIds: string[];
  frtManufacturersUniqueIds: string[];
  componentCapacitiesUniqueIds: string[];
  length: number | string;
  ancillaryTypeUniqueId: string;
  manufacturersUniqueIds: string[];
  calibresUniqueIds: string[];
  serialNumber: string;
  compositionsUniqueIds: string[];
  proofHousesUniqueIds: string[];
  fireCycleId: string;
  exhibitNumber: string;
  statesUniqueIds: string[];
  ownerUniqueId: string;
  keeperUniqueId: string;
  uniqueIds?: string[];
  ammunitionTypeUniqueId: string;
  munitionTypeUniqueId: string;
  expiryDateFrom: string | null;
  expiryDateTo: string | null;
  componentTypeUniqueId: string;
  componentmanufacturersUniqueIds: string[];
  legalityUniqueId: string;
  capacity: string;
  customFields?: ISearchFormCustomFieldValue[];
  ownerName: string;
  keeperName: string;
  minLatitude: number | undefined;
  maxLatitude: number | undefined;
  minLongitude: number | undefined;
  maxLongitude: number | undefined;

  [key: string]: any;
}

export const initialAncillariesSearchFormValues: ISearchAncillariesFormValues = {
  preset: undefined,
  artId: '',
  ancillaryDictionaryUniqueId: '',
  lotNumber: '',
  batchNumber: '',
  factoriesUniqueIds: [],
  manufacturerUniqueId: '',
  manufactureDateFrom: null,
  manufactureDateTo: null,
  functionalTypesUniqueIds: [],
  cartridgeCompositionsUniqueIds: [],
  coloursOfPrimerSealantUniqueIds: [],
  projectileShapesUniqueIds: [],
  shotSizesUniqueIds: [],
  munitionTypesUniqueIds: [],
  munitionTypeUniqueId: '',
  munitionVelocitiesUniqueIds: [],
  headstampYear: '',
  alphanumericalMarkins: '',
  hostTypesUniqueIds: [],
  magnificationsUniqueIds: [],
  utilitiesUniqueIds: [],
  reticlesUniqueIds: [],
  lightColoursUniqueIds: [],
  accessoryCapacities: [],
  frtTypesUniqueIds: [],
  frtMakesUniqueIds: [],
  frtModelsUniqueIds: [],
  frtManufacturersUniqueIds: [],
  componentCapacitiesUniqueIds: [],
  length: '',
  ancillaryTypeUniqueId: '',
  manufacturersUniqueIds: [],
  productName: '',
  calibresUniqueIds: [],
  serialNumber: '',
  marking: '',
  compositionsUniqueIds: [],
  proofHousesUniqueIds: [],
  fireCycleId: '',
  exhibitNumber: '',
  statesUniqueIds: [],
  ownerUniqueId: '',
  keeperUniqueId: '',
  ammunitionTypeUniqueId: '',
  expiryDateFrom: null,
  expiryDateTo: null,
  componentTypeUniqueId: '',
  componentmanufacturersUniqueIds: [],
  legalityUniqueId: '',
  capacity: '',
  ownerName: '',
  keeperName: '',
  minLatitude: undefined,
  maxLatitude: undefined,
  minLongitude: undefined,
  maxLongitude: undefined,
  customFields: [],
};

export interface ISearchLegalEntityAncillariesFormValues {
  legalUniqueId: string;
  ancillaryUniqueId: string;
  ancillaryTypeUniqueId: string;
  fireCycleId: string;
  marking: string;
  statesUniqueIds: string[];
}

export const initialLegalEntityAncillariesSearchFormValues: ISearchLegalEntityAncillariesFormValues =
  {
    legalUniqueId: '',
    ancillaryUniqueId: '',
    ancillaryTypeUniqueId: '',
    fireCycleId: '',
    marking: '',
    statesUniqueIds: [],
  };

export interface ISearchLegalEntityAncillaryGroupFormValues {
  ancillaryType: string;
}

export const initialLegalEntityAncillaryGroupSearchFormValues: ISearchLegalEntityAncillaryGroupFormValues =
  {
    ancillaryType: '',
  };

export interface ILegalEntityGroupFormValues {
  uniqueId: string;
  name: string;
  firearmIds: string[];
}

export interface ILegalEntityAncillaryGroupFormValues {
  uniqueId: string;
  name: string;
  ancillariesIds: string[];
}

export interface ILegalEntityEditGroupFormValues {
  uniqueId: string;
  name: string;
}

export const initialLegalEntityGroupFormValues = {
  name: '',
};

export const initialLegalEntityAncillaryGroupFormValues = {
  name: '',
};

export interface ISearchGroupFirearmFormValues {
  serialNumber: string;
  makeUniqueId: string;
  modelUniqueId: string;
  caliberUniqueId: string;
}

export const initialGroupFirearmSearchFormValues: ISearchGroupFirearmFormValues = {
  serialNumber: '',
  makeUniqueId: '',
  modelUniqueId: '',
  caliberUniqueId: '',
};

export const initialGroupAncillarySearchFormValues: ISearchAncillaryFormValues = {
  ancillarySpecificTypeUniqueId: '',
  ancillaryType: '',
  firearmType: '',
  serialNumber: '',
  lotNumber: '',
  batchNumber: '',
  manufacturersUniqueIds: [],
};

export interface ISearchUserFormValues {
  displayUniqueId: string;
  email: string;
  firstName: string;
  lastName: string;
  statuses?: EUserStatus[];
  role: string;
  rolesUniqueIds: string[];
  phoneNumber: string;
}

export const initialUserSearchFormValues: ISearchUserFormValues = {
  displayUniqueId: '',
  email: '',
  firstName: '',
  lastName: '',
  statuses: undefined,
  role: '',
  rolesUniqueIds: [],
  phoneNumber: '',
};

interface IRolesObject {
  [key: string]: undefined | boolean;
}

interface IPrefixGroupObject {
  [key: string]: undefined | boolean;
}

export interface IUserFormValues {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumberCodeDictionaryUniqueId: string;
  phoneNumberValue: string;
  roleIds: string[];
  formRoleIds: IRolesObject;
  formPrefixGroupIds: IPrefixGroupObject;
  formOrganisationCategoryIds: IPrefixGroupObject;
  formWeaponLicencingAuthority: string;
  prefixGroupIds: string[];
  organisationCategoryIds: string[];
  licenceAuthorityIds: string[];
  allLicenceAuthorities: boolean;
  password: string;
  version: number | undefined;
}

export interface IUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: {
    codeDictionaryUniqueId: string;
    value: string;
  };
  roleIds: string[];
  prefixGroupIds: string[];
  organisationCategoryIds: string[];
  licenceAuthorityIds: string[];
  allLicenceAuthorities: boolean;
  password: string;
}

export interface IUserUpdateRequest {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: {
    codeDictionaryUniqueId: string;
    value: string;
  };
  roleIds: string[];
  prefixGroupIds: string[];
  organisationCategoryIds: string[];
  licenceAuthorityIds: string[];
  allLicenceAuthorities: boolean;
}

export const initialUserFormValues: IUserFormValues = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumberCodeDictionaryUniqueId: '',
  phoneNumberValue: '',
  roleIds: [],
  prefixGroupIds: [],
  organisationCategoryIds: [],
  formRoleIds: {},
  formPrefixGroupIds: {},
  formOrganisationCategoryIds: {},
  formWeaponLicencingAuthority: '',
  allLicenceAuthorities: false,
  licenceAuthorityIds: [],
  password: '',
  version: undefined,
};

export interface ISearchFirearmsFormValues {
  serialNumber: string;
  marking: string;
  stateUniqueId: string;
  makeUniqueId: string;
  modelUniqueId: string;
  legalityUniqueId: string;
  manufacturerUniqueId: string;
  caliberUniqueId: string;
  groupUniqueId: string;
}

export const initialSearchFirearmsFormValues: ISearchFirearmsFormValues = {
  serialNumber: '',
  marking: '',
  stateUniqueId: '',
  makeUniqueId: '',
  modelUniqueId: '',
  legalityUniqueId: '',
  manufacturerUniqueId: '',
  caliberUniqueId: '',
  groupUniqueId: '',
};

export interface ISearchTransactionFormValues {
  statesUniqueIds: string[];
  transactionDateFrom: string | Date | number | null;
  transactionDateTo: string | Date | number | null;
}

export const initialTransactionSearchFormValues: ISearchTransactionFormValues = {
  statesUniqueIds: [],
  transactionDateFrom: '',
  transactionDateTo: '',
};

export const initialCalibreFormValues: IFRTCalibreFormValues = {
  caliberUniqueId: '',
  barrelLength: '',
  shots: '',
  barrelTypeUniqueId: '',
};

interface IProductCodeObject {
  productCode: string;
}

export interface IFRTNotesFormValues {
  ACT: string;
  AKA: string;
  BAR: string;
  CAL: string;
  IMG: string;
  IMP: string;
  MAK: string;
  MAN: string;
  MDT: string;
  MOD: string;
  MRK: string;
  REM: string;
  SER: string;
  SHT: string;
  TYP: string;
}

export interface IFRTFormValues extends IFRTNotesFormValues {
  makeUniqueId: string;
  modelUniqueId: string;
  manufacturerUniqueId: string;
  countryUniqueId: string;
  typeUniqueId: string;
  actionUniqueId: string;
  productCodes: IProductCodeObject[];
}

export const initialFRTNotesFormValues: IFRTNotesFormValues = {
  MAK: '',
  MOD: '',
  MAN: '',
  ACT: '',
  CAL: '',
  SHT: '',
  SER: '',
  AKA: '',
  MRK: '',
  REM: '',
  BAR: '',
  IMP: '',
  TYP: '',
  IMG: '',
  MDT: '',
};

export const initialFRTFormValues: IFRTFormValues = {
  makeUniqueId: '',
  modelUniqueId: '',
  manufacturerUniqueId: '',
  countryUniqueId: '',
  typeUniqueId: '',
  actionUniqueId: '',
  productCodes: [],
  ...initialFRTNotesFormValues,
};

export interface IFRTLocalNoteObject {
  type: string;
  description: string;
}

export interface IFRTLocalPatchRequest {
  notes: IFRTLocalNoteObject[];
}

export interface IFRTLocalUpdateRequest extends IFRTLocalPatchRequest {
  makeUniqueId: string;
  modelUniqueId: string;
  manufacturerUniqueId: string;
  actionUniqueId: string;
  typeUniqueId: string;
  countryUniqueId: string;
  productCodes: string[];
}

export const initialAttachmentFormValues = {
  transactionTypeUniqueId: '',
  attachmentName: '',
};

export interface IAttachment {
  attachmentName: string;
  file: File;
}

export interface IAttachmentFormValues {
  transactionUniqueId: string;
  attachmentName: string;
}

export interface IAttachmentForm {
  onCancel?: () => void;
  onSubmit: (arg: IAttachmentFormValues) => void;
  initialFormValues?: IAttachmentFormValues | null;
  usedTransactions: IShortTransaction[];
  transactionType?: IDictionary[] | null;
}

export interface ICommentInTransactionBody {
  title: string;
  comment: string;
}

export const initialCommentInTransactionFormValues = {
  title: '',
  comment: '',
};

export interface ISearchLegalEntityLicencesFormValues {
  weaponLicenceNumber: string;
  statusUniqueId: string;
}

export const initialLegalEntityLicencesSearchFormValues: ISearchLegalEntityLicencesFormValues =
  {
    weaponLicenceNumber: '',
    statusUniqueId: '',
  };

export interface ISearchLegalAttachmentsFormValues {
  createDateFrom: Date | null;
  createDateTo: Date | null;
  groupBy: ELegalAttachmentsGroupBy;
}

export const initialLegalAttachmentsSearchFormValues: ISearchLegalAttachmentsFormValues = {
  createDateFrom: null,
  createDateTo: null,
  groupBy: 'NONE' as ELegalAttachmentsGroupBy.NONE,
};
